<template>
  <svg
    v-if="points.length >= 2"
    class="sparkline"
    :viewBox="`0 0 ${width} ${height}`"
    :width="width"
    :height="height"
    aria-hidden="true"
  >
    <path class="sparkline__area" :d="areaPath" />
    <!-- pathLength normalizes the line to length 1 so consumers can run dash-draw
         animations (stroke-dasharray: 1) without measuring the real path. -->
    <path class="sparkline__line" :d="linePath" pathLength="1" :stroke-width="strokeWidth" />
    <circle class="sparkline__dot" :cx="endPoint!.x" :cy="endPoint!.y" :r="dotRadius" />
  </svg>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface SparklineProps {
  values: number[];
  width?: number;
  height?: number;
}

const props = withDefaults(defineProps<SparklineProps>(), { width: 110, height: 40 });

const strokeWidth = 2;
const dotRadius = 3;

const finiteValues = computed(() => props.values.filter((value) => Number.isFinite(value)));

const points = computed(() => {
  const values = finiteValues.value;
  if (values.length < 2) return [];

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  // Insets keep the stroke and the end dot inside the viewBox; the area baseline sits a bit
  // below the lowest point so a flat series still reads as a filled shape.
  const left = dotRadius + 1;
  const right = props.width - (dotRadius + 1);
  const top = dotRadius + strokeWidth;
  const bottom = props.height - (dotRadius + strokeWidth) - 4;

  return values.map((value, index) => ({
    x: round1(left + (index / (values.length - 1)) * (right - left)),
    y: round1(span === 0 ? (top + bottom) / 2 : bottom - ((value - min) / span) * (bottom - top)),
  }));
});

const linePath = computed(() => smoothPath(points.value));

const areaPath = computed(() => {
  const line = points.value;
  if (line.length < 2) return '';
  const baseline = props.height - (dotRadius + 1);
  return `${smoothPath(line)} L ${line[line.length - 1].x},${baseline} L ${line[0].x},${baseline} Z`;
});

// Index access instead of .at(-1): this lib also compiles under renter-app, whose TS lib
// target predates ES2022.
const endPoint = computed(() => points.value[points.value.length - 1] ?? null);

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

// Catmull-Rom control points, so the mini chart curves like the full Chart.js trend charts
// (tension 0.4) instead of reading as a different, jagged dataset.
function smoothPath(line: { x: number; y: number }[]): string {
  if (line.length < 2) return '';

  const segments = [`M ${line[0].x},${line[0].y}`];

  for (let i = 0; i < line.length - 1; i++) {
    const previous = line[i - 1] ?? line[i];
    const start = line[i];
    const end = line[i + 1];
    const next = line[i + 2] ?? end;

    const c1x = round1(start.x + (end.x - previous.x) / 6);
    const c1y = round1(start.y + (end.y - previous.y) / 6);
    const c2x = round1(end.x - (next.x - start.x) / 6);
    const c2y = round1(end.y - (next.y - start.y) / 6);

    segments.push(`C ${c1x},${c1y} ${c2x},${c2y} ${end.x},${end.y}`);
  }

  return segments.join(' ');
}
</script>

<style scoped lang="scss">
.sparkline {
  display: block;
  // Brand accent by default; set `color` on the component to recolor the whole chart.
  color: var(--t-color-orange-500);
}

.sparkline__line {
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
}

.sparkline__area {
  fill: currentColor;
  opacity: 0.12;
}

.sparkline__dot {
  fill: currentColor;
}
</style>
